import {
  ActionIcon,
  Affix,
  Button,
  Drawer,
  Group,
  Loader,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import {
  IconMessageCircle,
  IconSend,
  IconSparkles,
  IconX,
} from "@tabler/icons-react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { ComboboxItemPrice } from "~/types";

import classes from "./kiko-doctor-profile-assistant.module.scss";

type AssistantMessage = {
  id: string;
  role: "assistant" | "user";
  text: string;
};

type MessageBlock = {
  kind: "paragraph" | "bullets" | "numbered";
  lines: string[];
};

type DoctorAssistantContext = {
  doctorId: string;
  profileId?: string;
  slug?: string;
  name?: string;
  specialization?: string;
  specialties?: {
    label: string;
    value: string;
  }[];
  location?: string;
  consultationModes?: string[];
  services?: ComboboxItemPrice[];
  partnerSlug?: string;
};

interface KikoDoctorProfileAssistantProps {
  doctor?: DoctorAssistantContext;
  language: string;
  disabled?: boolean;
}

const SESSION_KEY_PREFIX = "laruche_kiko_doctor_session";

const copy = {
  en: {
    title: "Kiko booking assistant",
    launcher: "Need help booking?",
    discoveryTitle: "Find the right specialist with Kiko",
    discoveryLauncher: "Help me choose a specialist",
    intro: (doctorName?: string) =>
      doctorName
        ? `Hi, I am Kiko. I can help with ${doctorName}'s services, consultation options, and booking steps.`
        : "Hi, I am Kiko. Tell me what kind of care you need, and I can help you find a specialist.",
    placeholder: "Ask about this doctor or booking...",
    discoveryPlaceholder: "Describe what kind of care you need...",
    send: "Send",
    doctorLink: "View doctor profile",
    bookingLink: "Open booking page",
    thinking: "Kiko is checking...",
    error: "Kiko could not respond right now. Please try again.",
    quickActions: [
      "What does this doctor treat?",
      "How much does it cost?",
      "Can I book online?",
      "Help me choose a service",
    ],
    discoveryActions: [
      "Help me choose a specialist",
      "Find a dermatologist",
      "I don't know which doctor I need",
    ],
  },
  fr: {
    title: "Assistant de réservation Kiko",
    launcher: "Besoin d'aide ?",
    discoveryTitle: "Trouvez le bon spécialiste avec Kiko",
    discoveryLauncher: "Aidez-moi à choisir un spécialiste",
    intro: (doctorName?: string) =>
      doctorName
        ? `Bonjour, je suis Kiko. Je peux vous aider avec les services, les options de consultation et les étapes de réservation de ${doctorName}.`
        : "Bonjour, je suis Kiko. Décrivez le type de soins dont vous avez besoin et je peux vous aider à trouver un spécialiste.",
    placeholder: "Posez une question sur ce médecin...",
    discoveryPlaceholder: "Décrivez le type de soins dont vous avez besoin...",
    send: "Envoyer",
    doctorLink: "Voir le profil du médecin",
    bookingLink: "Ouvrir la page de réservation",
    thinking: "Kiko vérifie...",
    error: "Kiko ne peut pas répondre pour le moment. Veuillez réessayer.",
    quickActions: [
      "Que traite ce médecin ?",
      "Combien coûte la consultation ?",
      "Puis-je consulter en ligne ?",
      "Aidez-moi à choisir un service",
    ],
    discoveryActions: [
      "Aidez-moi à choisir un spécialiste",
      "Trouver un dermatologue",
      "Je ne sais pas quel médecin consulter",
    ],
  },
};

const createId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getSessionKey = (doctorId: string) => `${SESSION_KEY_PREFIX}:${doctorId}`;

const getSessionId = (doctorId: string) => {
  try {
    const key = getSessionKey(doctorId);
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    const sessionId = createId();
    window.sessionStorage.setItem(key, sessionId);
    return sessionId;
  } catch {
    return createId();
  }
};

const getLocalizedCopy = (language: string) =>
  language?.toLowerCase().startsWith("fr") ? copy.fr : copy.en;

const assistantResponseFromBody = (body: any, fallback: string) =>
  body?.data?.kikoResponse ||
  body?.data?.bookingConfirmation?.payment_link ||
  body?.error?.message ||
  fallback;

const isBookingLink = (url: string) =>
  /^https:\/\/(?:appointment|rendezvous)\.laruche\.health(?:\/|$)/i.test(url);

const formatMessageBlocks = (value: string): MessageBlock[] => {
  const blocks: MessageBlock[] = [];
  let paragraphBreak = false;
  for (const rawLine of value.replace(/\r\n/g, "\n").split("\n")) {
    const line = rawLine.trim();
    if (!line) {
      paragraphBreak = true;
      continue;
    }
    const bullet = line.match(/^[-*•]\s+(.+)$/);
    const numbered = line.match(/^\d+[.)]\s+(.+)$/);
    const kind = bullet ? "bullets" : numbered ? "numbered" : "paragraph";
    const content = bullet?.[1] || numbered?.[1] || line;
    const previous = blocks[blocks.length - 1];
    if (previous?.kind === kind && kind !== "paragraph" && !paragraphBreak) {
      previous.lines.push(content);
    } else if (previous?.kind === "paragraph" && kind === "paragraph" && !paragraphBreak) {
      previous.lines.push(content);
    } else {
      blocks.push({ kind, lines: [content] });
    }
    paragraphBreak = false;
  }
  return blocks;
};

const renderInlineText = (value: string, labels: typeof copy.en) =>
  value
    .split(/(\*\*[^*]+\*\*|\[[^\]]+\]\(https:\/\/[^\s)]+\)|https:\/\/[^\s)]+)/g)
    .map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }

      const markdownLink = part.match(/^\[([^\]]+)\]\((https:\/\/[^\s)]+)\)$/);
      const rawUrl = markdownLink?.[2] || (part.startsWith("https://") ? part : "");
      const url = rawUrl.replace(/[.,!?;:]+$/, "");
      if (!url || !isBookingLink(url)) return part;

      const label = markdownLink?.[1] ||
        (/\/doctor\//.test(url) ? labels.doctorLink : labels.bookingLink);
      return (
        <Fragment key={index}>
          <a href={url} target="_blank" rel="noopener noreferrer" className={classes.messageLink}>
            {label} ↗
          </a>
          {rawUrl.slice(url.length)}
        </Fragment>
      );
    });

const renderAssistantMessage = (value: string, labels: typeof copy.en) =>
  formatMessageBlocks(value).map((block, index) => {
    if (block.kind === "bullets" || block.kind === "numbered") {
      const List = block.kind === "bullets" ? "ul" : "ol";
      return (
        <List key={index} className={classes.messageList}>
          {block.lines.map((line, itemIndex) => (
            <li key={itemIndex}>{renderInlineText(line, labels)}</li>
          ))}
        </List>
      );
    }
    return (
      <p key={index} className={classes.messageParagraph}>
        {block.lines.map((line, lineIndex) => (
          <Fragment key={lineIndex}>
            {lineIndex > 0 ? <br /> : null}
            {renderInlineText(line, labels)}
          </Fragment>
        ))}
      </p>
    );
  });

export const KikoDoctorProfileAssistant = ({
  doctor,
  language,
  disabled,
}: KikoDoctorProfileAssistantProps) => {
  const [opened, { open, close }] = useDisclosure(false);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const doctorId = doctor?.doctorId || "discovery";
  const text = useMemo(() => getLocalizedCopy(language), [language]);
  const introText = useMemo(
    () => text.intro(doctor?.name),
    [doctor?.name, text]
  );
  const [messages, setMessages] = useState<AssistantMessage[]>([
    {
      id: "intro",
      role: "assistant",
      text: introText,
    },
  ]);
  const [draft, setDraft] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const sessionIdRef = useRef<string>("");
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setMessages(current =>
      current.length === 1 && current[0].id === "intro"
        ? [{ id: "intro", role: "assistant", text: introText }]
        : current
    );
  }, [introText]);

  useEffect(() => {
    sessionIdRef.current = getSessionId(doctorId);
  }, [doctorId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: "end" });
  }, [messages, isLoading]);

  const sendMessage = async (messageText: string) => {
    const trimmed = messageText.trim();
    if (!trimmed || isLoading || disabled) return;

    setDraft("");
    setIsLoading(true);
    setMessages(current => [
      ...current,
      { id: createId(), role: "user", text: trimmed },
    ]);

    try {
      const response = await fetch("/api/kiko-doctor-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: trimmed,
          sessionId: sessionIdRef.current || getSessionId(doctorId),
          language,
          pageUrl: window.location.href,
          doctor: doctor
            ? {
                ...doctor,
                specialties: doctor.specialties?.map(specialty => ({
                  label: specialty.label,
                  value: specialty.value,
                })),
                services: doctor.services?.map(service => ({
                  label: service.label,
                  price: service.price,
                  originalPrice: service.originalPrice,
                  currency: service.currency,
                  duration: service.duration,
                  category: service.category,
                })),
                pageUrl: window.location.href,
              }
            : undefined,
        }),
      });
      const body = await response.json();
      if (body?.data?.sessionId) {
        sessionIdRef.current = body.data.sessionId;
        window.sessionStorage.setItem(
          getSessionKey(doctorId),
          body.data.sessionId
        );
      }

      setMessages(current => [
        ...current,
        {
          id: body?.data?.id || createId(),
          role: "assistant",
          text: assistantResponseFromBody(body, text.error),
        },
      ]);
    } catch {
      setMessages(current => [
        ...current,
        { id: createId(), role: "assistant", text: text.error },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  if ((doctor && !doctor.doctorId) || disabled) return null;

  return (
    <>
      {!opened ? (
        <Affix position={{ bottom: 24, right: 24 }} className={classes.launcher}>
          <Button
            className={classes["launcher-button"]}
            radius="xl"
            size="md"
            leftSection={<IconSparkles size={18} />}
            onClick={open}>
            {doctor ? text.launcher : text.discoveryLauncher}
          </Button>
        </Affix>
      ) : null}

      <Drawer
        opened={opened}
        onClose={close}
        title={
          <Group gap="xs">
            <IconMessageCircle size={18} />
            <Text fw={600}>{doctor ? text.title : text.discoveryTitle}</Text>
          </Group>
        }
        position={isMobile ? "bottom" : "right"}
        size={isMobile ? "min(88dvh, 720px)" : 460}
        radius={isMobile ? "lg" : 0}
        overlayProps={{ backgroundOpacity: 0.25, blur: 2 }}
        closeButtonProps={{ icon: <IconX size={18} /> }}>
        <div className={classes["drawer-body"]}>
          <Stack className={classes.messages} gap="sm" role="log" aria-live="polite" aria-label={doctor ? text.title : text.discoveryTitle}>
            {messages.map(message => (
              <div
                key={message.id}
                className={`${classes.message} ${
                  message.role === "user"
                    ? classes["user-message"]
                    : classes["assistant-message"]
                }`}>
                {message.role === "assistant"
                  ? renderAssistantMessage(message.text, text)
                  : message.text}
              </div>
            ))}

            {messages.length === 1 ? <div className={classes["quick-actions"]}>
              {(doctor ? text.quickActions : text.discoveryActions).map(action => (
                <Button
                  key={action}
                  type="button"
                  variant="light"
                  size="xs"
                  radius="xl"
                  onClick={() => sendMessage(action)}
                  disabled={isLoading}>
                  {action}
                </Button>
              ))}
            </div> : null}

            {isLoading ? (
              <Group gap="xs" c="dimmed">
                <Loader size={14} />
                <Text fz={13}>{text.thinking}</Text>
              </Group>
            ) : null}
            <div ref={messagesEndRef} />
          </Stack>

          <form
            className={classes["input-row"]}
            onSubmit={event => {
              event.preventDefault();
              sendMessage(draft);
            }}>
            <Textarea
              autosize
              minRows={1}
              maxRows={4}
              value={draft}
              placeholder={doctor ? text.placeholder : text.discoveryPlaceholder}
              onChange={event => setDraft(event.currentTarget.value)}
              onKeyDown={event => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  sendMessage(draft);
                }
              }}
              maxLength={1200}
              disabled={isLoading}
            />
            <ActionIcon
              type="submit"
              size={42}
              radius="xl"
              variant="filled"
              aria-label={text.send}
              disabled={!draft.trim() || isLoading}>
              <IconSend size={18} />
            </ActionIcon>
          </form>
        </div>
      </Drawer>
    </>
  );
};
